/**
 * useResidualProgress — single hook that owns ALL async + lifecycle
 * effects for the Residual TARA v2 surface.
 *
 * Owns the Residual TARA lifecycle against the `/api/v1/residual/*`
 * backend, item-by-item (the legacy `Step10ResidualTARA.jsx`
 * front-end was retired 2026-05-27 — this hook is now the canonical
 * orchestrator):
 *
 *   1. Initial fetch     — `getResidualProgress` on mount once
 *                          sessionId + backendService are ready.
 *   2. Phase polling     — 3s `setInterval` while `current_phase` is
 *                          one of the three `ANALYSIS_PHASES`.
 *   3. Evidence polling  — 3s `setInterval` while
 *                          `current_phase === 'requirements_evaluation'`
 *                          (GitHub evidence collection progress).
 *   4. Auto-start risk   — when phase first transitions to
 *                          `risk_analysis`, fire
 *                          `continueResidualWorkflow` once (the
 *                          backend expects an explicit kick-off).
 *   5. Complete signal   — when phase first transitions to
 *                          `completed`, call
 *                          `workflow.stepNavigation.markStepCompleted(10)`
 *                          + `updateStepValidation(10, …)` once.
 *
 * Returns a bag of async action handlers the phase components call
 * directly. Each handler refetches progress after a successful
 * mutation so the reducer stays in sync.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  continueResidualWorkflow,
  getResidualProgress,
  initializeResidualTARA,
  submitIndividualRiskDecision,
  updateControlImplementation,
  updateRequirementFulfillment,
} from '../../../api/residualTara';
import { getResidualEvidenceStatus } from '../../../api/workflow';
import type { ResidualAction } from '../state/residualReducer';
import { ANALYSIS_PHASES, ResidualState } from '../state/types';

export interface UseResidualProgressInput {
  sessionId: string | null | undefined;
  /** Presence guard — legacy code only fetches when backendService is
   *  defined. We mirror that gate so any context-ready timing
   *  assumptions hold. */
  backendService: unknown;
  /** Slice from `useWorkflowMachine()` — the v2 page calls
   *  `markStepCompleted(10)` / `updateStepValidation(10, …)` on
   *  the one-time completion signal. Optional because some
   *  test/storybook setups may not provide it. */
  workflowStepNavigation?: {
    markStepCompleted?: (id: number) => void;
    updateStepValidation?: (id: number, validation: Record<string, unknown>) => void;
  } | null;
  state: ResidualState;
  dispatch: React.Dispatch<ResidualAction>;
}

export interface ResidualActions {
  /** Re-fetch the residual progress payload manually. */
  refetch: () => Promise<void>;
  /** Initialize the residual workflow (first-time setup). */
  initialize: () => Promise<void>;
  /** Advance the backend state machine to the next phase. */
  continueWorkflow: () => Promise<void>;
  /** Save an evidence record for a single control. */
  updateControl: (controlId: string, data: unknown) => Promise<void>;
  /** Override a single requirement's fulfillment status. */
  updateRequirement: (requirementId: string, data: unknown) => Promise<void>;
  /** Submit a treatment decision for a single risk. */
  submitDecision: (riskId: string, decision: unknown) => Promise<void>;
}

export function useResidualProgress({
  sessionId,
  backendService,
  workflowStepNavigation,
  state,
  dispatch,
}: UseResidualProgressInput): ResidualActions {
  const autoStartedRiskAnalysisRef = useRef(false);
  const completedSignalRef = useRef(false);

  const fetchProgress = useCallback(async (): Promise<void> => {
    if (!sessionId || !backendService) return;
    try {
      const data = await getResidualProgress(sessionId);
      dispatch({ type: 'HYDRATE_FROM_BACKEND', payload: data as never });
    } catch (e: any) {
      dispatch({
        type: 'SET_ERROR',
        payload: e?.message ?? 'Failed to fetch residual progress',
      });
    }
  }, [sessionId, backendService, dispatch]);

  // ── Effect 1: initial fetch ──────────────────────────────────
  useEffect(() => {
    if (!sessionId || !backendService) return;
    let cancelled = false;
    dispatch({ type: 'SET_LOADING', payload: true });
    fetchProgress().finally(() => {
      if (!cancelled) dispatch({ type: 'SET_LOADING', payload: false });
    });
    return () => {
      cancelled = true;
    };
  }, [sessionId, backendService, fetchProgress, dispatch]);

  const currentPhase = state.backendPhase;
  const isAnalysisPhase = !!currentPhase && ANALYSIS_PHASES.includes(currentPhase);

  // ── Effect 2: phase polling (3s during analysis phases) ──────
  useEffect(() => {
    if (!isAnalysisPhase) return;
    const interval = window.setInterval(() => {
      fetchProgress();
    }, 3000);
    return () => window.clearInterval(interval);
  }, [isAnalysisPhase, fetchProgress]);

  // ── Effect 3: evidence polling (requirements_evaluation only) ─
  useEffect(() => {
    if (currentPhase !== 'requirements_evaluation' || !sessionId) return;
    let cancelled = false;
    const pollOnce = async () => {
      try {
        const data = await getResidualEvidenceStatus(sessionId);
        if (!cancelled) dispatch({ type: 'SET_EVIDENCE_STATUS', payload: data as never });
      } catch {
        // Swallow — phase polling will surface real failures
      }
    };
    pollOnce();
    const interval = window.setInterval(pollOnce, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [currentPhase, sessionId, dispatch]);

  // ── Effect 4a: reset auto-start guard when phase leaves risk_analysis ─
  useEffect(() => {
    if (currentPhase !== 'risk_analysis') autoStartedRiskAnalysisRef.current = false;
  }, [currentPhase]);

  // ── Effect 4b: auto-start risk_analysis once ─────────────────
  useEffect(() => {
    if (currentPhase !== 'risk_analysis') return;
    if (autoStartedRiskAnalysisRef.current) return;
    if (!sessionId || !backendService) return;
    autoStartedRiskAnalysisRef.current = true;
    continueResidualWorkflow(sessionId).catch(() => {
      // Swallow — polling will recover the new phase on next tick
      autoStartedRiskAnalysisRef.current = false;
    });
  }, [currentPhase, sessionId, backendService]);

  // ── Effect 5: complete signal (one-time) ─────────────────────
  useEffect(() => {
    if (currentPhase !== 'completed') return;
    if (completedSignalRef.current) return;
    if (!workflowStepNavigation?.markStepCompleted) return;
    completedSignalRef.current = true;
    workflowStepNavigation.markStepCompleted(10);
    workflowStepNavigation.updateStepValidation?.(10, {
      isValid: true,
      isCompleted: true,
    });
  }, [currentPhase, workflowStepNavigation]);

  // ── Action handlers ──────────────────────────────────────────
  const refetch = fetchProgress;

  const initialize = useCallback(async () => {
    if (!sessionId) return;
    await initializeResidualTARA(sessionId);
    await fetchProgress();
  }, [sessionId, fetchProgress]);

  const continueWorkflow = useCallback(async () => {
    if (!sessionId) return;
    await continueResidualWorkflow(sessionId);
    await fetchProgress();
  }, [sessionId, fetchProgress]);

  const updateControl = useCallback(
    async (controlId: string, data: unknown) => {
      if (!sessionId) return;
      await updateControlImplementation(sessionId, controlId, data);
      await fetchProgress();
    },
    [sessionId, fetchProgress],
  );

  const updateRequirement = useCallback(
    async (requirementId: string, data: unknown) => {
      if (!sessionId) return;
      await updateRequirementFulfillment(sessionId, requirementId, data);
      await fetchProgress();
    },
    [sessionId, fetchProgress],
  );

  const submitDecision = useCallback(
    async (riskId: string, decision: unknown) => {
      if (!sessionId) return;
      await submitIndividualRiskDecision(sessionId, riskId, decision);
      await fetchProgress();
    },
    [sessionId, fetchProgress],
  );

  return useMemo<ResidualActions>(
    () => ({
      refetch,
      initialize,
      continueWorkflow,
      updateControl,
      updateRequirement,
      submitDecision,
    }),
    [refetch, initialize, continueWorkflow, updateControl, updateRequirement, submitDecision],
  );
}
